import { useDisclosure } from "@caspeco/casper-ui-library.components.modal";
import { useReservation } from "api/api-hooks/use-create-reservation";
import { useRefreshBooking } from "api/api-hooks/use-refresh-booking";
import ConfirmationDialog from "components/confirm-dialog";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { error as logError, warn } from "logger/logger";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Routes } from "routes";
import { BookingStatus } from "types/booking-status";
import SingleButtonDialog from "./single-button-dialog";

export const BookingValidator = () => {
    const { t } = useTranslation();
    const bookingId = useBookingId();
    const { data: booking, isLoading, refetch } = useReservation();
    const refreshBooking = useRefreshBooking();
    const location = useLocation();
    const { system, unitId } = useParams();
    const [showExpiredDialog, setShowExpiredDialog] = useState(false);
    const { isOpen, onClose, onOpen } = useDisclosure();
    const navigate = useNavigate();
    const { isActivityBooking } = useBookingStateContext();

    const validationState = useRef({
        hasAttemptedRefresh: false,
        isValidating: false,
    });

    useEffect(() => {
        if (!bookingId || isLoading || !booking || validationState.current.isValidating) return;
        const validateBooking = async () => {
            validationState.current.isValidating = true;

            try {
                // Expired booking that hasn't been refreshed yet - try to refresh it
                if (
                    booking.status === BookingStatus.Expired &&
                    !validationState.current.hasAttemptedRefresh
                ) {
                    warn("Booking expired, attempting to refresh...");
                    validationState.current.hasAttemptedRefresh = true;

                    try {
                        await refreshBooking.mutateAsync();
                        const { data: refreshedBooking } = await refetch();

                        // Show dialog if refresh didn't work
                        if (refreshedBooking?.status !== BookingStatus.BookingInProgress) {
                            setShowExpiredDialog(true);
                        }
                    } catch (error) {
                        logError("Error refreshing booking:", error);
                        setShowExpiredDialog(true);
                    }
                }
                // non-in-progress booking should not refresh
                else if (
                    booking.status !== BookingStatus.BookingInProgress &&
                    booking.status !== BookingStatus.WaitList &&
                    booking.status !== BookingStatus.Web
                ) {
                    warn("Booking is not in progress, showing expired dialog.");
                    setShowExpiredDialog(true);
                } else if (booking.isFinalized()) {
                    warn(
                        "Booking is already finalized – prompting user to view booking or start a new one."
                    );
                    onOpen();
                }
            } finally {
                validationState.current.isValidating = false;
            }
        };

        void validateBooking();
    }, [bookingId, booking, isLoading, refreshBooking, refetch, onOpen]);

    const handleDialogClose = () => {
        setShowExpiredDialog(false);

        let redirectPath = `/${system}/${unitId}`;
        if (location.pathname.includes(Routes.ACTIVITY_BOOKING)) {
            redirectPath += `/${Routes.ACTIVITY_BOOKING}`;
        }

        void navigate(redirectPath, { state: { intercept: false } });
    };

    const startOver = () => {
        let bookMoreUrl = `/${system}/${booking?.unitId}`;
        if (isActivityBooking) bookMoreUrl += "/activity-booking";
        void navigate(bookMoreUrl, { state: { intercept: false } });
    };

    const goToBooking = () => void navigate(`/booking/${system}/${bookingId}`);

    return (
        <>
            <SingleButtonDialog
                isOpen={showExpiredDialog}
                title={t("validation.sessionExpired.title")}
                message={t("validation.sessionExpired.message")}
                buttonText={t("validation.ok")}
                onAction={handleDialogClose}
                onClose={handleDialogClose}
            />
            <ConfirmationDialog
                isOpen={isOpen}
                title={t("validation.alreadyFinalized.title")}
                message={t("validation.alreadyFinalized.message")}
                primaryAction={startOver}
                secondaryAction={goToBooking}
                onClose={onClose}
                primaryButtonText={t("validation.alreadyFinalized.bookMore")}
                secondaryButtonText={t("validation.alreadyFinalized.viewBooking")}
                reverseButtons={true}
            />
        </>
    );
};
